import React, { ReactElement, useRef } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import CustomTextfield from '../form/CustomTextfield';
import { gray80, red50, tertiaryButtonBackgroundColor } from '../../../constants/colors';
import { boldMd, regularSm } from '../../../constants/location';
import { CHOICE_MAX_COUNT } from '../../../constants/enum';
import { itemChoiceLabel } from '../../../constants/itemValidation';
import { ItemChoiceType } from '../../../types/common';

/**
 * 選択肢設定エディタ（#3075）。
 *
 * ラジオボタン・プルダウン型の項目で、選択肢（表示名＋出力値）を編集する。
 * 表示名は画面に出す文字列、出力値は result_data・CSV に入る値。
 * 出力値が空なら表示名がそのまま出力値になるため、通常は表示名だけ埋めればよい。
 *
 * 並び順はそのまま画面の表示順になるため、ドラッグで入れ替えられるようにしている。
 */

type ChoiceRowProps = {
  id: string;
  choice: ItemChoiceType;
  index: number;
  disabled: boolean;
  canDelete: boolean;
  onChange: (index: number, next: ItemChoiceType) => void;
  onDelete: (index: number) => void;
};

const ChoiceRow: React.FC<ChoiceRowProps> = ({
  id,
  choice,
  index,
  disabled,
  canDelete,
  onChange,
  onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}
    >
      {!disabled ? (
        <IconButton
          sx={{ cursor: isDragging ? 'grabbing' : 'grab', padding: '2px' }}
          {...attributes}
          {...listeners}
        >
          <DragIndicatorIcon sx={{ color: '#90A1B9', fontSize: '20px' }} />
        </IconButton>
      ) : (
        <Box sx={{ width: '26px' }} />
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CustomTextfield
          value={choice.label}
          onChange={(e): void => onChange(index, { ...choice, label: e.target.value })}
          formMb="0px"
          labelText=""
          inputWidth="100%"
          height="40px"
          inputPaddingIs10px8px
          placeholder={itemChoiceLabel.labelPlaceholder}
          disabled={disabled}
          borderColor="#CAD5E2"
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CustomTextfield
          value={choice.value}
          onChange={(e): void => onChange(index, { ...choice, value: e.target.value })}
          formMb="0px"
          labelText=""
          inputWidth="100%"
          height="40px"
          inputPaddingIs10px8px
          placeholder={itemChoiceLabel.valuePlaceholder}
          disabled={disabled}
          borderColor="#CAD5E2"
        />
      </Box>

      {/* 選択肢は最低1件必要なため、残り1件のときは削除させない */}
      <Tooltip title={canDelete ? '' : '選択肢は1つ以上必要です'}>
        <span>
          <IconButton
            onClick={(): void => onDelete(index)}
            disabled={disabled || !canDelete}
            sx={{ padding: '4px' }}
          >
            <DeleteIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

type Props = {
  choices: ItemChoiceType[];
  onChange: (choices: ItemChoiceType[]) => void;
  disabled?: boolean;
  /** バリデーションエラー文言。null ならエラーなし */
  errorMessage?: string | null;
};

const ChoiceEditor = ({
  choices,
  onChange,
  disabled = false,
  errorMessage = null,
}: Props): ReactElement => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // 並び替えの識別子。行ごとに固定のIDを振り、並び替えてもIDが中身についてまわるようにする。
  // インデックスをIDにすると、並び替えのたびにIDと中身の対応が入れ替わり、
  // dnd-kit からは「要素が消えて別の要素が現れた」ように見えてドロップ時に一瞬元へ戻る。
  // 選択肢は編集途中に空欄が重複しうるため、値そのものはIDに使えない。
  const rowIdsRef = useRef<string[]>([]);
  const nextRowIdRef = useRef(0);
  if (rowIdsRef.current.length !== choices.length) {
    // 追加・削除で件数が変わったときだけIDを補う。既存行のIDは維持する
    const current = [...rowIdsRef.current];
    while (current.length < choices.length) {
      nextRowIdRef.current += 1;
      current.push(`choice-${nextRowIdRef.current}`);
    }
    rowIdsRef.current = current.slice(0, choices.length);
  }
  const rowIds = rowIdsRef.current;

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = rowIds.indexOf(String(active.id));
    const newIndex = rowIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    // 選択肢と一緒にIDも並び替え、行とIDの対応を保つ
    rowIdsRef.current = arrayMove(rowIds, oldIndex, newIndex);
    onChange(arrayMove(choices, oldIndex, newIndex));
  };

  const handleRowChange = (index: number, next: ItemChoiceType): void => {
    onChange(choices.map((choice, i) => (i === index ? next : choice)));
  };

  const handleDelete = (index: number): void => {
    // 削除した行のIDも同時に落とす。件数合わせ（末尾を切る）任せにすると
    // 途中の行を消したときに残った行とIDの対応がずれる
    rowIdsRef.current = rowIds.filter((_, i) => i !== index);
    onChange(choices.filter((_, i) => i !== index));
  };

  const handleAdd = (): void => {
    if (choices.length >= CHOICE_MAX_COUNT) return;
    onChange([...choices, { label: '', value: '' }]);
  };

  return (
    <Box>
      <Typography sx={{ ...boldMd, color: gray80, marginBottom: '4px' }}>
        {itemChoiceLabel.section}
      </Typography>
      <Typography sx={{ ...regularSm, color: gray80, marginBottom: '12px' }}>
        {itemChoiceLabel.description}
      </Typography>

      {/* 入力欄が2列あるため、どちらが何かを見出しで示す */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <Box sx={{ width: '26px' }} />
        <Typography sx={{ ...regularSm, color: gray80, flex: 1 }}>
          {itemChoiceLabel.label}
        </Typography>
        <Typography sx={{ ...regularSm, color: gray80, flex: 1 }}>
          {itemChoiceLabel.value}（任意）
        </Typography>
        <Box sx={{ width: '28px' }} />
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
          {choices.map((choice, index) => (
            <ChoiceRow
              key={rowIds[index]}
              id={rowIds[index]}
              choice={choice}
              index={index}
              disabled={disabled}
              canDelete={choices.length > 1}
              onChange={handleRowChange}
              onDelete={handleDelete}
            />
          ))}
        </SortableContext>
      </DndContext>

      <Button
        onClick={handleAdd}
        disabled={disabled || choices.length >= CHOICE_MAX_COUNT}
        startIcon={<AddIcon />}
        sx={{ ...tertiaryButtonBackgroundColor, height: '32px', padding: '4px 12px' }}
      >
        <Typography sx={{ ...regularSm, color: gray80 }}>{itemChoiceLabel.addButton}</Typography>
      </Button>

      {errorMessage && (
        <Typography sx={{ fontSize: '12px', color: red50, marginTop: '6px' }}>
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default ChoiceEditor;
